import { useCallback } from 'react';
import { IBillingAddress } from '../../../../interfaces/IBillingAddress';
import { Nullable } from '@jamesgmarks/utilities';

/**
 * Type guard to check if an object is a valid IBillingAddress
 */
const isBillingAddress = (obj: unknown): obj is IBillingAddress => {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const addr = obj as Record<string, unknown>;

  // Just check for a couple key properties that should exist
  return (
    (typeof addr.country === 'string' || addr.country == null) &&
    (typeof addr.province === 'string' || addr.province == null)
  );
};

/**
 * Utility function to format billing address for display
 */
export const useFormatBillingAddress = () => {
  return useCallback((billingAddress: Nullable<IBillingAddress | object>): string => {
    if (!billingAddress) {
      return 'No billing address';
    }

    if (!isBillingAddress(billingAddress)) {
      return 'Invalid billing address';
    }

    // Build address parts functionally
    const addressLines = [
      billingAddress.organization,
      billingAddress.street1,
      billingAddress.street2,
    ].filter(Boolean);

    // Build city line
    const cityLine = [billingAddress.city, billingAddress.province, billingAddress.postalCode]
      .filter(Boolean)
      .join(', ');

    // Combine all parts
    const allParts = [
      ...addressLines,
      cityLine,
      billingAddress.country,
    ].filter(Boolean);

    return allParts.length > 0 ? allParts.join(', ') : 'No billing address';
  }, []);
};

/**
 * Get dialog title based on current mode
 */
export const getDialogTitle = (mode: string): string => {
  const titles: Record<string, string> = {
    'setup': 'Create Bulk Subscription Credits - Setup',
    'client-selection': 'Create Bulk Subscription Credits - Select Subscriptions',
    'charge-selection': 'Create Bulk Subscription Credits - Select Charges',
    'review': 'Create Bulk Subscription Credits - Review',
    'splits-review': 'Create Bulk Subscription Credits - Review Splits',
    'created-notes': 'Bulk Subscription Credits Created',
  };

  return titles[mode] || 'Create Bulk Subscription Credits';
};

